<template>
  <div class="studio" data-page="studio">
    <section class="studio__hero c-container">
      <AppPicture
        class="hero__image"
        :data="{
          url: '/assets/img/studio/studio_00.jpg',
          alt: 'Pic of Gesia studio',
        }"
        :isLazy="false"
        data-enter="hero"
      />
      <h2 data-enter="title">Between Engineering and Architecture</h2>
      <p data-anim="paragraph">
        Gesia is an innovative and dynamic reality, capable of understanding
         and achieving our clients perception with ambitious and highly personalized
         projects following the given brief.
      </p>
      <p data-anim="paragraph">
         It’s engineering discipline as well
         as the architectural genius gives life to different projects ranging from
         residential to hospitality, from industrial production to the
         manufacturing of a piece of furniture.
      </p>
    </section>
    <section class="studio__table c-container">
      <div class="table__description">
        <h3 data-anim="title">The pen is on the table</h3>
        <p data-anim="paragraph">
          On the same table, our team includes a variety of different specialists
          that help Gesia bring its projects to life.<br><br>We all have different perceptions
          and points of view that are open to discussion with the rest of the team.
        </p>
      </div>
      <AppPicture
        class="table__image"
        :data="{
          url: '/assets/img/studio/studio_01.jpg',
          alt: 'Pic of Gesia table',
        }"
      />
    </section>
    <section class="studio__team c-container">
      <h3
        style="z-index: 10"
        data-anim="title"
      >team</h3>
      <ul class="team__list">
        <li
          :class="[
            'team__box',
            i === team.team.length - 1 ? 'team__box--last' : ''
          ]"
          v-for="(member, i) in team.team"
          :key="i"
        >
          <img class="team__box-img" :src="member.url" :alt="member.name">
          <p class="team__box-title" data-anim="title">{{ member.name }}</p>
          <p data-anim="paragraph">{{ member.role }}</p>
          <a :href="`mailto:${member.mail}`" data-anim="paragraph">{{ member.mail }}</a>
        </li>
      </ul>
    </section>
    <section class="studio__collabo c-container">
      <h3 data-anim="title">Collaborators</h3>
      <ul class="team__list">
        <li
          :class="[
            'team__box',
            i === team.team.length - 1 ? 'team__box--last' : ''
          ]"
          v-for="(member, i) in team.collabo"
          :key="i"
        >
          <img class="team__box-img" :src="member.url" :alt="member.name">
          <p class="team__box-title" data-anim="title">{{ member.name }}</p>
          <p data-anim="paragraph">{{ member.role }}</p>
          <a :href="`mailto:${member.mail}`" data-anim="paragraph">{{ member.mail }}</a>
        </li>
      </ul>
    </section>
  </div>
</template>

<script>
import TimelineLite from 'gsap/TimelineLite';
import { qs } from '@/scripts/utils';
import title from '@/scripts/animations/title';
import pictureAnimation from '@/scripts/animations/pictureAnimation';

import AppPicture from '@/components/AppPicture.vue';

import team from '@/assets/data/team.json';


export default {
  name: 'studio',

  components: {
    AppPicture,
  },

  data() {
    return {
      team,
    };
  },

  mounted() {
    this.enterAnimation();
  },

  methods: {
    enterAnimation() {
      const elToTween = qs('[data-enter="hero"]', this.$el);
      const textToTween = qs('[data-enter="title"]', this.$el);

      const tl = new TimelineLite();

      tl.addLabel('start')
        .add(pictureAnimation(elToTween), 'start')
        .add(title(textToTween), 'start+=0.8');

      elToTween.removeAttribute('data-enter');
      elToTween.removeAttribute('data-enter');
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~sass-mq/mq";
@import '../style/colors';
@import '../style/variables';
@import '../style/functions';
@import '../style/mixins';

@keyframes ghostmove {
  0% {
    transform: translate3d(0, 0, 0);
  }
  25% {
    transform: translate3d(5px, -7px, 0);
  }
  50% {
    transform: translate3d(3px, 4px, 0);
  }
  75% {
    transform: translate3d(-5px, -7px, 0);
  }
  100% {
    transform: translate3d(0, 0, 0);
  }
}

.studio {
  h3 {
    text-transform: uppercase;
    font-weight: bold;
    font-size: font-size(m);
    padding-bottom: 20px;
  }

  &__hero {
    .hero__image {
      width: 100%;
      margin-bottom: 40px;

      box-sizing: border-box;

      @include aspect-ratio(16, 9);

      overflow: hidden;

      @include mq(mobileL) {
        width: calc(100% - 160px);
        margin: 0 0 60px 160px;
      }
    }

    h2 {
      font-size: font-size(xl);
      -webkit-text-stroke: 1.5px $color-text;
      color: transparent;
      letter-spacing: 1px;
    }

    p {
      display: inline-block;
      width: 80%;
      padding-left: 30px;

      @include mq(mobileL) {
        padding-left: 60px;
      }

      &:first-of-type {
        margin: 40px 0 20px;
      }
    }
  }

  &__table {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    flex-flow: row wrap;

    .table__description {
      position: relative;
      width: 100%;

      flex: 0 0 100%;
      order: 2;
      margin-top: 40px;

      @include mq(mobileL) {
        width: 30%;
        flex: 0 0 30%;
        order: 1;
      }

      p {
        color: rgba($color-text, 0.75);
      }
    }

    .table__image {
      position: relative;
      width: 100%;
      padding-bottom: 60px;

      flex: 0 0 100%;
      order: 1;

      @include aspect-ratio(3, 2);

      @include mq(mobileL) {
        width: 60%;
        padding-bottom: 0;

        flex: 0 0 60%;
        order: 2;
      }
    }
  }

  &__team,
  &__collabo {
    position: relative;
    width: 100%;

    h3 {
      padding-bottom: 40px;
    }

    .team__list {
      width: 100%;
      padding-bottom: 80px;

      display: flex;
      align-items: flex-start;
      justify-content: flex-start;
      flex-flow: row wrap;
    }

    .team__box {
      position: relative;
      width: 100%;
      flex: 0 0 100%;
      padding: 15px 0 15px 40px;
      box-sizing: border-box;

      white-space: nowrap;
      // border: 1px solid #dedede;
      border-left: 0;
      margin-top: -1px;
      background-color: transparent;
      transition: background-color 0.4s ease-in-out;

      @include mq(mobileL) {
        width: 50%;
        flex: 0 0 50%;
        padding: 3% 60px 6%;
      }

      @include mq(ipadL) {
        width: 33%;
        flex: 0 0 33%;
      }

      @include mq(d) {
        width: 25%;
        flex: 0 0 25%;
      }

      @include mq(dxl) {
        width: 20%;
        flex: 0 0 20%;
      }

      p,
      a {
        display: block;
        position: relative;

        color: rgba($color-text, 0.75);
        font-size: font-size(m);


        z-index: 5;

        @include mq(dm) {
          font-size: font-size(s);
        }
      }

      & .team__box-title {
        margin-bottom: 20px;

        font-size: font-size(l);
        color: $color-text;
      }

      @include mq($and: $is-not-touch-device) {
        &:hover {
          background-color: #fcfcfc;

          .team__box-img {
            opacity: 0.5;
          }
        }
      }


      & .team__box-img {
        display: inline-block;
        position: absolute;
        top: -140px;
        left: 40%;
        width: 70%;

        z-index: 0;
        opacity: 0;

        transition: opacity 0.4s ease-in-out;
        animation: ghostmove 20s linear 0s infinite alternate both;

        pointer-events: none;
      }
    }
  }
}
</style>
